import React, { useState } from 'react';
import { Alert, Box, Typography } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { NewsCitizenModel } from '../../../../api-service/clients';
import { TypographySettings } from '../../../../components/config';
import { FormDialog } from '../../../../components/form-dialog';
import { ConfirmDialog } from '../../../../components/confirm-dialog/confirm-dialog';
import { TextField } from '../../../../components/text-field/text-field';
import { Checkbox } from '../../../../components/checkbox/checkbox';

const useStyles = makeStyles()(theme => ({
  citizenList: {
    display: 'flex',
    flexDirection: 'column',
    maxHeight: 320,
    overflowY: 'auto',
    marginTop: theme.spacing(1),
  },
  heading: {
    fontWeight: TypographySettings.DefaultFontSemiBold,
    marginTop: theme.spacing(2),
  },
}));

export interface INewsRecipientsDialogComponentProps {
  title?: string;
  showToAll: boolean;
  recipients?: NewsCitizenModel[];
  hasFailed: boolean;
  isSaving: boolean;
  onUpdateRecipients?: (citizenIds: string[]) => void;
  onClose: () => void;
}

export function NewsRecipientsDialogComponent(props: INewsRecipientsDialogComponentProps) {
  const { title, showToAll, recipients, hasFailed, isSaving, onUpdateRecipients, onClose } = props;
  const { classes } = useStyles();
  const [removedCitizenIds, setRemovedCitizenIds] = useState<string[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);

  const allRecipients = recipients ?? [];
  const matchingRecipients = allRecipients.filter(citizen => matchesSearch(citizen, searchTerm));
  const citizensToRemove = allRecipients.filter(citizen => removedCitizenIds.includes(citizen.citizenId));

  function toggleCitizen(citizenId: string) {
    setRemovedCitizenIds(current => (current.includes(citizenId) ? current.filter(id => id !== citizenId) : [...current, citizenId]));
  }

  function confirmUpdate() {
    setIsConfirmOpen(false);
    onUpdateRecipients?.(allRecipients.filter(citizen => !removedCitizenIds.includes(citizen.citizenId)).map(citizen => citizen.citizenId));
  }

  function renderContent() {
    if (hasFailed) {
      return <Alert severity="error">Der opstod en fejl ved hentning af data. Prøv igen senere.</Alert>;
    }
    if (!recipients) {
      return <Typography>Indlæser...</Typography>;
    }
    if (showToAll) {
      return <Typography>Denne nyhed vises til alle borgere</Typography>;
    }
    if (allRecipients.length === 0) {
      return <Typography>Ingen borgere er tilknyttet denne nyhed</Typography>;
    }

    return (
      <>
        <TextField label="Søg borger" value={searchTerm} multiline={false} onChange={event => setSearchTerm(event.target.value)} />
        <Typography className={classes.heading}>Borgere der modtager</Typography>
        <Box className={classes.citizenList}>
          {matchingRecipients.map(citizen =>
            onUpdateRecipients ? (
              <Checkbox
                key={citizen.citizenId}
                label={describeCitizen(citizen)}
                checked={!removedCitizenIds.includes(citizen.citizenId)}
                onChange={() => toggleCitizen(citizen.citizenId)}
              />
            ) : (
              <Typography key={citizen.citizenId}>{describeCitizen(citizen)}</Typography>
            )
          )}
        </Box>
        {citizensToRemove.length > 0 && (
          <>
            <Typography className={classes.heading}>Borgere du er ved at fjerne</Typography>
            <Box className={classes.citizenList}>
              {citizensToRemove.map(citizen => (
                <Typography key={citizen.citizenId}>{describeCitizen(citizen)}</Typography>
              ))}
            </Box>
          </>
        )}
      </>
    );
  }

  return (
    <>
      <FormDialog
        open
        title={
          <>
            {title}
            <Typography variant="body2">Nyhedsmodtagere</Typography>
          </>
        }
        confirmButtonText={onUpdateRecipients ? 'Opdater modtagere' : 'Luk'}
        cancelButtonText="Annullér"
        onConfirmClick={onUpdateRecipients ? () => setIsConfirmOpen(true) : onClose}
        onCancelClick={onUpdateRecipients ? onClose : undefined}
        onClose={onClose}
        isValid={!onUpdateRecipients || (!showToAll && removedCitizenIds.length > 0)}
        isLoading={isSaving}
        dontCapitalizeButtonTexts
        maxWidth="md"
        fullWidth
      >
        {renderContent()}
      </FormDialog>
      <ConfirmDialog
        open={isConfirmOpen}
        type="question"
        title="Er du sikker på at du vil opdatere borgere som modtager denne nyhed?"
        confirmButtonText="Ja"
        cancelButtonText="Nej"
        onConfirmClick={confirmUpdate}
        onCancelClick={() => setIsConfirmOpen(false)}
      />
    </>
  );
}

function matchesSearch(citizen: NewsCitizenModel, searchTerm: string) {
  const term = searchTerm.trim().toLowerCase();
  return !term || (citizen.name ?? '').toLowerCase().includes(term) || (citizen.cpr ?? '').includes(term);
}

function describeCitizen(citizen: NewsCitizenModel) {
  return `${citizen.name ?? ''} (${citizen.cpr ?? ''})`;
}
